import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { backgroundColors, DbtResourceIcon, ResourceType } from '@dbt-labs/dbt-dag';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useEnvironmentSummary } from '@/hooks/discovery/environmentSummary';

interface PanelDetailsProps {
  projectId: number | null;
  environmentId: number | null;
}

interface RowProps extends React.ComponentPropsWithoutRef<'li'> {
  text: string;
  bgColor: string;
  count: number | undefined;
  resourceType: ResourceType;
}

const Row: React.FC<RowProps> = ({
  text,
  bgColor,
  count,
  resourceType,
  className,
  ...props
}) => {
  return (
    <li {...props} className={twMerge(className, 'flex')}>
      <span className={twMerge('flex-0 h-6 w-6 rounded-md pl-1.5', bgColor)}>
        <DbtResourceIcon resource={resourceType} size="xs" />
      </span>
      <span className="mx-2 flex-1 text-sm font-normal leading-6 text-fgDecorative">
        {text}
      </span>
      <span className="flex-0">{count}</span>
    </li>
  );
};

export const ProjectPanelGeneralTab: FC<PanelDetailsProps> = ({
  projectId,
  environmentId,
}) => {
  const { accountId } = useDbtCloudAccount();

  const environmentSummary = useEnvironmentSummary({
    accountId,
    environmentId: environmentId,
  }).data;

  if (!accountId || !projectId) return null;

  return (
    <>
      <div className="mt-4 text-xs font-medium text-fgAlt">Resources</div>
      <ul className="mt-4 space-y-2">
        <Row
          bgColor={backgroundColors.source}
          count={environmentSummary?.source}
          text="Sources"
          resourceType="source"
        />
        <Row
          bgColor={backgroundColors.model}
          count={environmentSummary?.model}
          text="Models"
          resourceType="model"
        />
        <Row
          bgColor={backgroundColors.semantic_model}
          count={environmentSummary?.semantic_model}
          text="Semantic Models"
          resourceType="semantic_model"
        />
        <Row
          bgColor={backgroundColors.seed}
          count={environmentSummary?.seed}
          text="Seeds"
          resourceType="seed"
        />
        <Row
          bgColor={backgroundColors.exposure}
          count={environmentSummary?.exposure}
          text="Exposures"
          resourceType="exposure"
        />
        <Row
          bgColor={backgroundColors.test}
          count={environmentSummary?.test}
          text="Tests"
          resourceType="test"
        />
        <Row
          bgColor={backgroundColors.metric}
          count={environmentSummary?.metric}
          text="Metrics"
          resourceType="metric"
        />
        <Row
          bgColor={backgroundColors.snapshot}
          count={environmentSummary?.snapshot}
          text="Snapshots"
          resourceType="snapshot"
        />
        <Row
          bgColor={backgroundColors.macro}
          count={environmentSummary?.macro}
          text="Macros"
          resourceType="macro"
        />
        <Row
          bgColor={backgroundColors.saved_query}
          count={environmentSummary?.saved_query}
          text="Saved Queries"
          resourceType="saved_query"
        />
      </ul>
    </>
  );
};
